import { ComcastErrorType, triggerSentryError } from '../helpers.js'
import { createScreenLogger, clearScreenLogger } from '../screenLogger.js'
import { createMemoryLogger } from '../memoryLogger.js'

const tvKeys = {
  8: 'BACK',
  13: 'ENTER',
  19: 'PAUSE',
  27: 'EXIT',
  32: 'SPACE',
  37: 'LEFT',
  38: 'UP',
  39: 'RIGHT',
  40: 'DOWN',
  166: 'BACK',
  204: 'CC',
  403: 'RED',
  404: 'GREEN',
  405: 'YELLOW',
  406: 'BLUE',
  227: 'RW',
  412: 'RW',
  413: 'STOP',
  415: 'PLAY',
  416: 'REC',
  417: 'FF',
  228: 'FF',
  427: 'CH_UP',
  428: 'CH_DOWN',
  447: 'VOL_DOWN',
  448: 'VOL_UP',
  449: 'MUTE',
  457: 'INFO',
  10009: 'BACK',
  10135: 'TOOLS',
  10182: 'EXIT',
  10190: 'PRECH',
  10252: 'PLAYPAUSE',
  179: 'PLAYPAUSE',
}

const letters = Array.from(Array(26))
  .map((e, i) => i + 65)
  .map((x) => String.fromCharCode(x))
const numbers = Array.from(Array(10)).map((e, i) => i.toString())
for (let i = 0; i < letters.length; i++) {
  tvKeys[i + 65] = letters[i]
}
for (let i = 0; i < numbers.length; i++) {
  tvKeys[i + 48] = numbers[i]
}

const enableOverlayPattern = [
  'UP',
  'UP',
  'DOWN',
  'DOWN',
  'LEFT',
  'RIGHT',
  'LEFT',
  'RIGHT',
]
let enableOverlayCurrentIndex = 0
const clearOverlayPattern = ['9', '9']
let clearOverlayCurrentIndex = 0
let isOverlayEnabled = false

function checkPattern(keyString, pattern, index, callback) {
  if (pattern.indexOf(keyString) < 0 || keyString !== pattern[index]) {
    index = 0
  } else {
    index++
  }

  if (pattern.length === index) {
    index = 0
    callback()
  }

  return index
}

/**
 * Process Key Event
 * @param {object} focusedComponent - The component to process the event.
 * @param {number} keyString - The keyString action produced by the mapping table above.
 */
export function startKeyEvent(focusedComponent, keyString, store) {
  enableOverlayCurrentIndex = checkPattern(
    keyString,
    enableOverlayPattern,
    enableOverlayCurrentIndex,
    function () {
      createScreenLogger()
      createMemoryLogger()
      store.dispatch({
        type: 'TOGGLE_OVERLAY',
      })
      isOverlayEnabled = true
    }
  )
  if (isOverlayEnabled) {
    clearOverlayCurrentIndex = checkPattern(
      keyString,
      clearOverlayPattern,
      clearOverlayCurrentIndex,
      function () {
        clearScreenLogger()
      }
    )
  }

  processKeyEvent(focusedComponent, keyString, {
    store,
  })
}

export function processKeyEvent(focusedComponent, keyString, { store }) {
  store.dispatch({
    type: 'SET_APP_INTERACTION',
  })

  if (store.getState().screensaver.showing) {
    store.dispatch({
      type: 'SCREENSAVER_VISIBILITY',
      payload: false,
    })
    return false
  } else {
    store.dispatch({
      type: 'SCREENSAVER_VISIBILITY',
      payload: false,
    })
  }

  if (
    !focusedComponent ||
    'undefined' === typeof focusedComponent ||
    !focusedComponent.onKeyEvent ||
    'undefined' === typeof focusedComponent.onKeyEvent
  ) {
    return false
  }

  // If the current loadingScreen state is true, end processKeyEvent.
  // Ignored if navigating up/down in sidepanel
  // TODO: This only applies for GA, so it should be behind an APP-Compile flag or split and moved into ga/utils. it could also be removed entirely.
  if (store.getState().loading.loadingScreen) {
    if (
      (keyString === 'UP' || keyString === 'DOWN') &&
      'SidePanel' === focusedComponent.constructor.name
    ) {
      // GO AHEAD
    } else {
      return
    }
  }

  let response = focusedComponent.onKeyEvent(keyString)

  if (false === response && 'undefined' !== focusedComponent.props.parent) {
    processKeyEvent(focusedComponent.props.parent, keyString, { store })
  }

  return false
}

/**
 * On Key Down
 * @param {store} store - The redux store to initialize with
 */
export function onKeyDown(store) {
  return (e) => {
    if (!window.focusedComponent?.handleBySpatialNavigation) {
      e.stopPropagation()
      e.preventDefault()
    }

    startKeyEvent(
      window.focusedComponent,
      tvKeys[e.keyCode.toString()],
      store,
      e
    )
  }
}

export function isKeyAlphaNumeric(keyString) {
  return isKeyAlpha(keyString) || isKeyNumeric(keyString)
}

export function isKeyAlpha(keyString) {
  return letters.includes(keyString)
}

export function isKeyNumeric(keyString) {
  return numbers.includes(keyString)
}

export function registerTizenNumericalKeys() {
  try {
    window.tizen.tvinputdevice.registerKeyBatch(
      Array(10)
        .fill()
        .map((_, i) => i.toString()),
      function () {
        //success
      },
      function (err) {
        console.error(err)
      }
    )
  } catch (error) {
    triggerSentryError(
      'could not register tizen numerical keys',
      'helpers.js',
      {
        error,
        errorType: ComcastErrorType.other,
      }
    )
  }
}

export function registerTizenMediaKeys() {
  window.tizen.tvinputdevice.registerKey('MediaPlay')
  window.tizen.tvinputdevice.registerKey('MediaPause')
  window.tizen.tvinputdevice.registerKey('MediaPlayPause')
  window.tizen.tvinputdevice.registerKey('MediaStop')
  window.tizen.tvinputdevice.registerKey('MediaFastForward')
  window.tizen.tvinputdevice.registerKey('MediaRewind')
}
